import { getPaymentNoticePdf, login } from '@/api/apiCommon';
import { EASYBUY_STEPS, NON_EASYBUY_STEPS, STEPS } from '@/core/checkoutSteps';
import { CompleteStatuses, normalizeDocuments, UPLOAD_SUBMIT_STATUSES } from '@/core/docs';
import { trackGTMEvent } from '@/core/helpers';
import { CheckoutFormDataService } from '@/core/services/checkoutFormDataService';
import { CompleteService } from '@/core/services/completeService';
import { LocalStorageService } from '@/core/services/localStorageService';
import { QuoteService } from '@/core/services/quoteService';
import { SelectedProductService } from '@/core/services/selectedProductService';
import { InsuranceCovers, TextConstants } from '@/enums/filters';
import { usePackagesStore } from "@/stores/packages";
import { useQuoteStore } from '@/stores/quote';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';


const STORAGE_KEY = 'stepper';

export const useStepperStore = defineStore('stepper', () => {
    const steps = ref({});
    const paymentRF = ref(null);
    const requiredDocs = ref({});
    const additionalDocs = ref({});
    const paymentViva = ref(null);
    const addKlarna = ref(null);
    const orderStatus = ref(null);
    const offerStatus = ref(null);
    const currentStep = ref(STEPS.CHECKOUT.value);
    const uploadStatus = ref(UPLOAD_SUBMIT_STATUSES.IDLE);
    const hasSpecifiedInfo = ref(null);
    const price = ref(null);

    const router = useRouter();
    const quoteStore = useQuoteStore();
    const quoteService = QuoteService();
    const packagesStore = usePackagesStore();
    const completeService = CompleteService();
    const selectedProductService = SelectedProductService();
    const checkoutFormDataService = CheckoutFormDataService();
    
    const isLastStep = computed(() => currentStep.value === steps.value.length);
    const isFirstStep = computed(() => currentStep.value === STEPS.CHECKOUT.value);

    const resetInsuranceCovers = () => {
        quoteService.store({ insuranceCovers: [InsuranceCovers.NaturalPhenomena] });
    };

    const initSteps = ({ forceUploadFlow = false, forcePaymentFlow = false } = {}) => {
        if (forceUploadFlow) {
            steps.value = NON_EASYBUY_STEPS;
            return;
        }
        if (forcePaymentFlow) {
            steps.value = EASYBUY_STEPS;
            return;
        }

        const isEasyBuy = selectedProductService.getEasyBuyStatus() ?? false;
        steps.value = isEasyBuy ? EASYBUY_STEPS : NON_EASYBUY_STEPS;
    };

    const syncStepsWithAcceptance = (status) => {
        const isEasyBuy = status === CompleteStatuses.ACCEPTED;
        selectedProductService.setEasyBuyStatus(isEasyBuy);
        steps.value = isEasyBuy ? EASYBUY_STEPS : NON_EASYBUY_STEPS;
    };
    
    const persist = () => {
        LocalStorageService.set(STORAGE_KEY, { currentStep: currentStep.value });
    }

    const goToStep = (step) => {
        const targetStep = Math.min(Math.max(step, STEPS.CHECKOUT.value), steps.value.length);

        if (currentStep.value === STEPS.CHECKOUT.value && targetStep > STEPS.CHECKOUT.value) {
            resetInsuranceCovers();
        }

        currentStep.value = targetStep;
        persist();
    }

    const resetSteps = () => {
        goToStep(STEPS.CHECKOUT.value);
        orderStatus.value = null;
        offerStatus.value = null;
        requiredDocs.value = {};
        additionalDocs.value = {};
        uploadStatus.value = UPLOAD_SUBMIT_STATUSES.IDLE;
    };

    const setUploadRequirements = (documents, additionalDocuments = {}, status = null) => {
        requiredDocs.value = normalizeDocuments(documents);
        additionalDocs.value = additionalDocuments ?? {};
        orderStatus.value = CompleteStatuses.AWAITING_DOCS;
        offerStatus.value = status;
    };

    const setPaymentData = (paymentCode = null, paymentLink = null, isKlarnaEligible = null) => {
        paymentRF.value = paymentCode;
        paymentViva.value = paymentLink;
        addKlarna.value = isKlarnaEligible;
    };

    const goToStepById = (stepId) => {
        const stepIndex = steps.value.findIndex(step => step?.id === stepId);
        if (stepIndex === -1) {
            return;
        }

        goToStep(stepIndex + 1);
    };

    const nextStep = async () => {
        try {
            const { fid } = quoteService.get();
            const dc = selectedProductService.getDc();
            const { vehicleType } = quoteService.get();

            const {
              step,
              status,
              price: completePrice,
              paymentCode,
              paymentLink,
              documents,
              isKlarnaEligible,
              hasSpecifiedInfo: specifiedInfo,
            } = await completeService.checkOrderStatus(fid, dc, vehicleType);

            hasSpecifiedInfo.value = specifiedInfo ?? null;
            price.value = completePrice ?? null;

            orderStatus.value = status;
            offerStatus.value = status;
            syncStepsWithAcceptance(status);
            const lastStep = steps.value.length;

            if (step != STEPS.PAYMENT.value) {
                const { vehicleType } = quoteService.get();
                return await router.push({
                    name: `${vehicleType}Form`,
                    params: { fid }
                });
            }

            if (status === CompleteStatuses.ACCEPTED) {
                goToStep(lastStep);
                setPaymentData(paymentCode, paymentLink, isKlarnaEligible);
                return;
            }

            requiredDocs.value = normalizeDocuments(documents);

            sendUploadDocumentsAnalyticsEvent(Object.keys(requiredDocs.value).length > 0);
            
            goToStep(Math.min(currentStep.value + 1, lastStep));
        } catch (err) {
            console.error('validateStep failed', err);
        }
    }

    const sendUploadDocumentsAnalyticsEvent = (uploadFiles) => {
        const selectedProduct = selectedProductService.getSelectedProduct()
        const offerCode = selectedProductService.getOfferCode();
        const quote = quoteService.get();
        const vehicleType = quote?.vehicleType ?? null;
        const roadAssistanceSelected = quote?.insuranceCovers.includes(InsuranceCovers.RoadsideAssistance) ?? false;

        trackGTMEvent({
        	"event": "complete",
            "type": uploadFiles ? "upload_files" : "docs_review",
        	"offer_code": offerCode,
        	"category": vehicleType,
        	"checkout_type": selectedProduct.easyBuy ? 'easy_buy' : 'non_easy_buy',
        	"complete_value": selectedProduct.price.toFixed(2),
        	"odikiExists": roadAssistanceSelected
        })

        if (Number(selectedProduct?.crossSell) > 0) {
            trackGTMEvent({
                "event": "anytime_cosmote_10_buy",
                "position": packagesStore.getPackagePosition(selectedProduct),
                "pb_id": quoteStore.quote.trackingData?.preBuyID,
                "offer_code": offerCode
            });
        }
    }


    const prevStep = () => {
        goToStep(currentStep.value - 1);
    };

    const submitPayment = (paymentMethodId) => {
        if (paymentViva.value?.includes('pay.hellasdirect.gr/pay')) {
            window.location.href = paymentViva.value;
        } else {
            window.location.href = paymentViva.value + `/viva_payment_method/${paymentMethodId}`;
        }
    }

    const downloadBankTransferPDF = async () => {
        let token = await login();
        let quote = quoteService.get();
        let formData = checkoutFormDataService.get();
        let selectedProduct = selectedProductService.getSelectedProduct();

        let startDate = new Date(selectedProductService.getStartDate())
        let startDateFormated = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`

        let resp = await getPaymentNoticePdf(token, { 
            "data": {
                name: formData.name + ' ' + formData.surname,
                licensePlate: formData.license_plate,
                rf: paymentRF.value,
                startDate: startDateFormated,
                packet: TextConstants[quote.insuranceType],
                totalAmount: selectedProduct.price
            }
        })

        const link = document.createElement('a')
        link.href = "data:application/pdf;base64," + resp.pdf
        link.download = 'payment-notification'
        link.click()
    }

    return {
        steps,
        currentStep,
        initSteps,
        isFirstStep,
        isLastStep,
        nextStep,
        prevStep,
        resetSteps,
        setUploadRequirements,
        goToStepById,
        downloadBankTransferPDF,
        submitPayment,
        setPaymentData,
        paymentRF,
        addKlarna,
        orderStatus,
        offerStatus,
        requiredDocs,
        additionalDocs,
        uploadStatus,
        hasSpecifiedInfo,
        price,
    }
})
